import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box, Heading, VStack, useToast, Flex, Tr, Table, Th, Td, Thead, Tbody,
    useColorMode, Button, IconButton, HStack, Spinner, Modal, ModalOverlay,
    ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    FormControl, FormLabel, Input, Textarea, useDisclosure, AlertDialog,
    AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody,
    AlertDialogFooter, Checkbox, Select, Slider, SliderTrack, SliderFilledTrack, SliderThumb,
    Badge, Text
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, DragHandleIcon } from '@chakra-ui/icons';
import Navigation from '../components/Navigation';
import axios from '../utils/axiosConfig';

// dnd-kit imports
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

const SortableRow = ({ item, children }) => {
    // make the entire row a sortable item; we will attach listeners to a handle
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        background: isDragging ? 'rgba(0,0,0,0.04)' : undefined,
    };

    return (
        <Tr ref={setNodeRef} style={style}>
            {/* Sort handle cell */}
            <Td width="48px" pr={0}>
                <IconButton
                    aria-label="Drag to reorder"
                    icon={<DragHandleIcon />}
                    size="sm"
                    variant="ghost"
                    cursor="grab"
                    {...attributes}
                    {...listeners}
                />
            </Td>
            {children}
        </Tr>
    );
};

const AdminModelsPage = () => {
    const { colorMode } = useColorMode();
    const toast = useToast();

    const [isAdmin, setIsAdmin] = useState(null);
    const [models, setModels] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editingModel, setEditingModel] = useState(null);
    const [form, setForm] = useState({ name: '', description: '', provider: '', value: '' });
    const [isSaving, setIsSaving] = useState(false);

    const {
        isOpen: isDeleteOpen,
        onOpen: onDeleteOpen,
        onClose: onDeleteClose,
    } = useDisclosure();
    const cancelRef = useRef();
    const [deletingModel, setDeletingModel] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const {
        isOpen: isUsageOpen,
        onOpen: onUsageOpen,
        onClose: onUsageClose,
    } = useDisclosure();
    const [usageModel, setUsageModel] = useState(null);

    // dnd sensors (mouse/touch + keyboard)
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor)
    );

    useEffect(() => {
        const checkAuthentication = async () => {
            try {
                const response = await axios.get('/api/protected/checkIsAdmin');
                setIsAdmin(response.data.isAdmin);
            } catch (error) {
                console.error('Error checking session:', error);
                setIsAdmin(false);
            }
        };
        checkAuthentication();
        listModels();
    }, []);

    const formatDateTime = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const listModels = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get('/api/admin/models');
            if (response.data?.success && response.data?.data?.models) {
                // Ensure they’re sorted by `order` from backend; fallback to createdAt
                const rows = response.data.data.models.slice().sort((a, b) => {
                    if (a.order != null && b.order != null) return a.order - b.order;
                    return new Date(a.createdAt) - new Date(b.createdAt);
                });
                setModels(rows);
            } else {
                toast({ title: 'Failed to retrieve models', status: 'error', description: 'Invalid response format' });
            }
        } catch (error) {
            console.error('Error fetching models:', error);
            toast({
                title: 'Failed to retrieve models',
                status: 'error',
                description: error.response?.data?.message || 'An error occurred',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // ---------- Add / Edit ----------
    const openAddModal = () => {
        setEditingModel(null);
        setForm({ name: '', description: '', provider: 'OpenAI', value: '' });
        onOpen();
    };

    const openEditModal = (model) => {
        setEditingModel(model);
        setForm({
            name: model.name || '',
            description: model.description || '',
            provider: model.provider || 'OpenAI',
            value: model.value || '',
            temperature: model.temperature || 0.7,
            maxTokens: model.maxTokens || 4096,
            inputType: model.inputType || 'text',
            outputType: model.outputType || 'text',
            realtime: model.realtime || false,
            useForAIInstructionsGeneration: model.useForAIInstructionsGeneration || false
        });
        onOpen();
    };

    const handleFormChange = (e) => {
        const { name, type, value, checked } = e.target;
        console.log(e.target);
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSave = async () => {
        if (!form.name?.trim()) return toast({ title: 'Name is required', status: 'warning' });
        if (!form.value?.trim()) return toast({ title: 'Value is required', status: 'warning' });

        setIsSaving(true);
        try {
            if (editingModel) {
                const { _id } = editingModel;
                const res = await axios.put(`/api/admin/models/${_id}`, form);
                if (res.data?.success) {
                    toast({ title: 'Model updated', status: 'success' });
                    setModels((prev) => prev.map((m) => (m._id === _id ? { ...m, ...res.data.data.model } : m)));
                    onClose();
                } else throw new Error(res.data?.message || 'Update failed');
            } else {
                const res = await axios.post('/api/admin/models', form);
                if (res.data?.success) {
                    toast({ title: 'Model created', status: 'success' });
                    setModels((prev) => [res.data.data.model, ...prev]);
                    onClose();
                } else throw new Error(res.data?.message || 'Create failed');
            }
        } catch (error) {
            console.error('Save error:', error);
            toast({
                title: 'Save failed',
                status: 'error',
                description: error.response?.data?.message || error.message || 'An error occurred',
            });
        } finally {
            setIsSaving(false);
        }
    };

    // ---------- Delete ----------
    const confirmDelete = (model) => {
        if ((model.usageCount || 0) > 0) {
            setUsageModel(model);
            onUsageOpen();
            toast({
                title: 'Model is in use',
                status: 'warning',
                description: 'Move these assistants to another model before deleting this one.',
            });
            return;
        }

        setDeletingModel(model);
        onDeleteOpen();
    };

    const handleDelete = async () => {
        if (!deletingModel) return;
        setIsDeleting(true);
        try {
            const res = await axios.delete(`/api/admin/models/${deletingModel._id}`);
            if (res.data?.success) {
                toast({ title: 'Model deleted', status: 'success' });
                setModels((prev) => prev.filter((m) => m._id !== deletingModel._id));
                onDeleteClose();
            } else throw new Error(res.data?.message || 'Delete failed');
        } catch (error) {
            console.error('Delete error:', error);
            if (error.response?.status === 409 && error.response?.data?.data?.model) {
                setUsageModel(error.response.data.data.model);
                onUsageOpen();
            }
            toast({
                title: 'Delete failed',
                status: 'error',
                description: error.response?.data?.message || error.message || 'An error occurred',
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const openUsageModal = (model) => {
        setUsageModel(model);
        onUsageOpen();
    };

    // ---------- Drag sorting ----------
    const ids = useMemo(() => models.map((m) => m._id), [models]);

    const onDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = models.findIndex((m) => m._id === active.id);
        const newIndex = models.findIndex((m) => m._id === over.id);

        const prev = models;
        const next = arrayMove(models, oldIndex, newIndex);
        setModels(next); // optimistic

        try {
            // Persist new order: send ids in their new order
            const res = await axios.put('/api/admin/models/bulk/reorder', {
                ids: next.map((m) => m._id),
            });
            if (!res.data?.success) throw new Error(res.data?.message || 'Reorder failed');
            // Optionally update local 'order' values from server response:
            if (res.data?.data?.models) setModels(res.data.data.models);
            toast({ title: 'Order updated', status: 'success' });
        } catch (err) {
            console.error('Reorder error:', err);
            setModels(prev); // revert
            toast({
                title: 'Failed to update order',
                status: 'error',
                description: err.response?.data?.message || err.message || 'An error occurred',
            });
        }
    };

    if (isAdmin === null || !isAdmin) return null;

    return (
        <Flex>
            <Navigation />
            <Box p={8} flex="1">
                <Flex justify="space-between" align="center" mb={6}>
                    <Heading size="lg" color={colorMode === 'light' ? 'gray.800' : 'white'}>
                        Models
                    </Heading>
                    <Button leftIcon={<AddIcon />} colorScheme="teal" onClick={openAddModal}>
                        Add Model
                    </Button>
                </Flex>

                <VStack spacing={4} align="stretch">
                    <Box borderWidth="1px" rounded="lg" overflow="hidden">
                        <DndContext
                            sensors={sensors}
                            onDragEnd={onDragEnd}
                            modifiers={[restrictToVerticalAxis]}
                        >
                            <Table variant="simple" size="lg" width="full">
                                <Thead
                                    position="sticky"
                                    top={0}
                                    bg={colorMode === 'light' ? 'gray.100' : 'gray.700'}
                                    zIndex={1}
                                >
                                    <Tr>
                                        <Th width="48px" />
                                        <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Name</Th>
                                        <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Description</Th>
                                        <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Provider</Th>
                                        <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Value</Th>
                                        <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Usage</Th>
                                        <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Created On</Th>
                                        <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Last Updated On</Th>
                                        <Th color={colorMode === 'light' ? 'gray.700' : 'gray.200'} textAlign="right">
                                            Actions
                                        </Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {isLoading ? (
                                        <Tr><Td colSpan={9}><HStack justify="center" py={10}><Spinner /></HStack></Td></Tr>
                                    ) : models.length === 0 ? (
                                        <Tr><Td colSpan={9} color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>No models found.</Td></Tr>
                                    ) : (
                                        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                                            {models.map((u) => (
                                                <SortableRow key={u._id} item={u}>
                                                    <Td color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>{u.name}</Td>
                                                    <Td color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>{u.description}</Td>
                                                    <Td color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>{u.provider}</Td>
                                                    <Td color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>{u.value}</Td>
                                                    <Td>
                                                        <Badge colorScheme={(u.usageCount || 0) > 0 ? 'orange' : 'green'}>
                                                            {(u.usageCount || 0) > 0 ? `${u.usageCount} assistant${u.usageCount === 1 ? '' : 's'}` : 'Unused'}
                                                        </Badge>
                                                    </Td>
                                                    <Td color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>{formatDateTime(u.createdAt)}</Td>
                                                    <Td color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>{formatDateTime(u.updatedAt)}</Td>
                                                    <Td>
                                                        <HStack justify="flex-end" spacing={2}>
                                                            {(u.usageCount || 0) > 0 && (
                                                                <Button size="sm" variant="outline" onClick={() => openUsageModal(u)}>
                                                                    View Usage
                                                                </Button>
                                                            )}
                                                            <IconButton aria-label="Edit" icon={<EditIcon />} size="sm" onClick={() => openEditModal(u)} />
                                                            <IconButton aria-label="Delete" icon={<DeleteIcon />} size="sm" colorScheme="red" variant="outline" onClick={() => confirmDelete(u)} />
                                                        </HStack>
                                                    </Td>
                                                </SortableRow>
                                            ))}
                                        </SortableContext>
                                    )}
                                </Tbody>
                            </Table>
                        </DndContext>
                    </Box>
                </VStack>
            </Box>

            {/* Add/Edit Modal */}
            <Modal isOpen={isOpen} onClose={() => (!isSaving ? onClose() : null)} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{editingModel ? 'Edit Model' : 'Add Model'}</ModalHeader>
                    <ModalCloseButton isDisabled={isSaving} />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel>Name</FormLabel>
                                <Input name="name" value={form.name} onChange={handleFormChange} placeholder="e.g. gpt-4o-mini" />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Description</FormLabel>
                                <Textarea name="description" value={form.description} onChange={handleFormChange} placeholder="Short description…" />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Provider</FormLabel>
                                <Select name="provider" value={form.provider || "OpenAI"} onChange={handleFormChange} >
                                    <option value="OpenAI">OpenAI</option>
                                </Select>
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Value</FormLabel>
                                <Input name="value" value={form.value} onChange={handleFormChange} placeholder="e.g. gpt-4o-mini-2024-07-18" />
                            </FormControl>
                            <HStack spacing={6} width="full" pt={4} >
                                <FormControl isRequired>
                                    <FormLabel>Default Temperature</FormLabel>
                                    <Slider
                                        min={0}
                                        max={1}
                                        step={0.1}
                                        value={form.temperature}
                                        onChange={(val) => setForm((prev) => ({ ...prev, temperature: val }))}
                                    >
                                        <SliderTrack bg="gray.200">
                                            <SliderFilledTrack bg="blue.500" />
                                        </SliderTrack>
                                        <SliderThumb />
                                    </Slider>
                                    <Box mt={2}>Value: {(form.temperature || 0.7).toFixed(1)}</Box>
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel>Default Max Tokens</FormLabel>
                                    <Input name="maxTokens" value={form.maxTokens || 4096} onChange={handleFormChange}   />
                                </FormControl>
                            </HStack>
                            <HStack spacing={6} width="full" pt={4} >
                                <FormControl isRequired>
                                    <FormLabel>Input Type</FormLabel>
                                    <Select name="inputType" value={form.inputType || "text"} onChange={handleFormChange} >
                                        <option value="text">Text</option>
                                        <option value="audio">Audio</option>
                                    </Select>
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel>Output Type</FormLabel>
                                    <Select name="outputType" value={form.outputType || "text"} onChange={handleFormChange} >
                                        <option value="text">Text</option>
                                        <option value="audio">Audio</option>
                                    </Select>
                                </FormControl>
                            </HStack>
                            <FormControl >
                                <FormLabel>Realtime API?</FormLabel>
                                <Checkbox name="realtime" isChecked={form.realtime || false} onChange={handleFormChange} />
                            </FormControl>
                            <FormControl >
                                <FormLabel>To use for AI Instruction Generation?</FormLabel>
                                <Checkbox name="useForAIInstructionsGeneration" isChecked={form.useForAIInstructionsGeneration || false} onChange={handleFormChange} />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <HStack>
                            <Button onClick={onClose} variant="ghost" isDisabled={isSaving}>Cancel</Button>
                            <Button colorScheme="teal" onClick={handleSave} isLoading={isSaving}>{editingModel ? 'Save Changes' : 'Create Model'}</Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Usage Details */}
            <Modal isOpen={isUsageOpen} onClose={onUsageClose} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Model Usage</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack align="stretch" spacing={4}>
                            <Box>
                                <Heading size="sm">{usageModel?.name || 'Model'}</Heading>
                                <Text fontSize="sm" color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>{usageModel?.value}</Text>
                            </Box>
                            <HStack spacing={3}>
                                <Badge colorScheme="orange">
                                    {usageModel?.usageCount || 0} assistant{usageModel?.usageCount === 1 ? '' : 's'}
                                </Badge>
                                <Badge colorScheme="blue">
                                    {usageModel?.usageUsersCount || 0} user account{usageModel?.usageUsersCount === 1 ? '' : 's'}
                                </Badge>
                            </HStack>
                            <Box borderWidth="1px" rounded="md" overflowX="auto">
                                <Table size="sm">
                                    <Thead bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}>
                                        <Tr>
                                            <Th>Assistant</Th>
                                            <Th>Assistant ID</Th>
                                            <Th>User</Th>
                                            <Th>User Email</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {(usageModel?.usageAssistants || []).map((item) => (
                                            <Tr key={item.assistantId}>
                                                <Td>{item.assistantName}</Td>
                                                <Td>{item.assistantId}</Td>
                                                <Td>{item.userName}</Td>
                                                <Td>{item.userEmail || '-'}</Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onUsageClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete Confirmation */}
            <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={() => (!isDeleting ? onDeleteClose() : null)}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete Model</AlertDialogHeader>
                        <AlertDialogBody>Are you sure you want to delete <b>{deletingModel?.name || 'this model'}</b>? This action cannot be undone.</AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onDeleteClose} isDisabled={isDeleting}>Cancel</Button>
                            <Button colorScheme="red" onClick={handleDelete} ml={3} isLoading={isDeleting}>Delete</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Flex>
    );
};

export default AdminModelsPage;
